import { defineMutator } from "@rocicorp/zero";
import { z } from "zod";
import { zql } from "../schema";
import type { MutateSurface } from "../message-builders";
import type { MutatorCtx } from "./messages-cores";
// eslint-disable-next-line no-restricted-imports -- legacy junk-drawer file; rename in plans/cached-singing-fiddle.md
import { generateId, requireWorkspaceRole } from "./helpers";
import { makeWorkspaceId, makeUserId } from "../types/branded-ids";

export const decideJoinRequestSchema = z.object({
  id: z.string(),
  decision: z.enum(["approved", "rejected"]),
});

export const cancelJoinRequestSchema = z.object({ id: z.string() });

export type DecideJoinRequestArgs = z.infer<typeof decideJoinRequestSchema>;

export interface DecideJoinRequestResult {
  /** The workspace this request targeted (server-side path only). */
  workspaceId: string;
  /** The user who submitted the request. */
  userId: string;
  /** The actor who decided (== ctx.sub on the server path). */
  decidedBy: string;
  /** Whether the request was approved or rejected. */
  decision: "approved" | "rejected";
}

/**
 * Shared core for `workspace_join_requests.decide`. Auth-checks the
 * caller, updates the request row's status, and returns the
 * `(workspaceId, userId, decision)` triple so the server-mutator
 * extension can fire side effects (insert workspace_members on
 * approve, emit decision notification to the requester) without
 * re-reading.
 *
 * Returns `null` on the client optimistic path (no row read) and when
 * the row is missing server-side — both short-circuit the override's
 * fan-out. An already-decided row THROWS ("Request is no longer
 * pending") rather than returning null, so a double-decide surfaces as
 * an error instead of a silent no-op.
 */
export async function decideJoinRequestCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: DecideJoinRequestArgs;
  ctx: MutatorCtx;
}): Promise<DecideJoinRequestResult | null> {
  let workspaceId: string | null = null;
  let requesterUserId: string | null = null;

  if (tx.location === "server") {
    if (!ctx?.sub) {
      throw new Error("Unauthorized: missing caller identity");
    }
    const row = await tx.run(
      zql.workspace_join_requests.where("id", args.id).one(),
    );
    if (!row) return null;
    // Auth FIRST: a caller with no admin authority over the target
    // workspace must not learn the request's state (already-decided vs
    // pending). Role check gates before the business-state check.
    await requireWorkspaceRole(
      tx,
      makeWorkspaceId(row.workspace_id),
      makeUserId(ctx.sub),
    );
    if (row.status !== "pending") {
      throw new Error("Request is no longer pending");
    }
    workspaceId = row.workspace_id;
    requesterUserId = row.user_id;
  }

  await tx.mutate.workspace_join_requests.update({
    id: args.id,
    status: args.decision,
    decided_at: Date.now(),
    decided_by: ctx?.sub,
  });

  if (
    tx.location !== "server" ||
    !workspaceId ||
    !requesterUserId ||
    !ctx?.sub
  ) {
    return null;
  }
  return {
    workspaceId,
    userId: requesterUserId,
    decidedBy: ctx.sub,
    decision: args.decision,
  };
}

/**
 * Mutators for the public-workspace request-to-join queue.
 *
 * The `create` mutator is hard-rejected on the public path — the only
 * legitimate writer is the regional API endpoint
 * `POST /api/workspaces/by-slug/:slug/request-join`, which validates
 * the public-mode policy + the email-domain allowlist before inserting
 * the row. Allowing a public `joinRequests.insert` would let a kicked
 * member spam an arbitrary workspace's queue.
 *
 * `decide` and `cancel` ARE legitimate public mutators:
 *   - decide: ADMIN_PLUS on the target workspace, approves or rejects.
 *     On approve, the server-mutator extension at
 *     `server/server-mutators.ts` does the regional-PG side effect
 *     (insert workspace_members row + auto-join public channels +
 *     emit notifications). The shared mutator below only updates the
 *     request row's status.
 *   - cancel: requester withdraws their own pending row.
 */

export const workspaceJoinRequestsMutators = {
  insert: defineMutator(
    z.object({
      id: z.string(),
      workspace_id: z.string(),
      user_id: z.string(),
      status: z.string().optional(),
      message: z.string().optional().nullable(),
      requested_at: z.number(),
    }),
    async () => {
      // Public path is closed. See module docstring above.
      throw new Error(
        "Unauthorized: use POST /api/workspaces/by-slug/:slug/request-join",
      );
    },
  ),
  decide: defineMutator(decideJoinRequestSchema, async (params) => {
    await decideJoinRequestCore(params);
  }),
  cancel: defineMutator(cancelJoinRequestSchema, async ({ tx, args, ctx }) => {
    if (tx.location === "server") {
      if (!ctx?.sub) {
        throw new Error("Unauthorized: missing caller identity");
      }
      const row = await tx.run(
        zql.workspace_join_requests.where("id", args.id).one(),
      );
      if (!row) return;
      if (row.user_id !== ctx.sub) {
        throw new Error("Unauthorized: not the requester");
      }
      if (row.status !== "pending") {
        // Idempotent — already cancelled / decided.
        return;
      }
    }
    await tx.mutate.workspace_join_requests.update({
      id: args.id,
      status: "cancelled",
      decided_at: Date.now(),
      decided_by: ctx?.sub,
    });
  }),
};

/**
 * Shared schema + core for the request-to-join entry point. The
 * server route in `server/routes/public-workspace-routes.ts` calls
 * this core inside `sql.begin(...)` after looking up the workspace by
 * slug. The shape mirrors the other action mutators that have a
 * server-extension companion (messageInsertCore, etc.) so the public
 * REST surface stays consistent with the Zero mutator family.
 */
export const requestJoinSchema = z.object({
  workspace_id: z.string(),
  user_id: z.string(),
  message: z.string().max(500).optional().nullable(),
});

export type RequestJoinArgs = z.infer<typeof requestJoinSchema>;

export function buildJoinRequestRow(args: RequestJoinArgs) {
  return {
    id: generateId(),
    workspace_id: args.workspace_id,
    user_id: args.user_id,
    status: "pending" as const,
    message: args.message ?? null,
    requested_at: Date.now(),
    decided_at: null,
    decided_by: null,
  };
}
